import { useCallback, useEffect, useRef } from "react";
import { animate, useDragControls, useMotionValue } from "framer-motion";

const EDGE = 8; // keep floating widgets at least this far from the screen edge

/**
 * Makes a fixed-position floating widget draggable (mouse + touch).
 * - Drag starts from whichever element calls `startDrag` on pointer down, so
 *   inner content (e.g. an open chat window) stays scrollable/selectable.
 * - A tap that didn't move still behaves as a normal click; wrap click
 *   handlers with `guardClick` so a drag doesn't also fire a click.
 * - The position is remembered per `storageKey` and kept inside the viewport.
 */
export default function useFloatingDrag(storageKey) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const controls = useDragControls();
  const elRef = useRef(null);
  const constraintsRef = useRef(null);
  const draggedRef = useRef(false);

  // Nudge the widget back on-screen if any part of it is outside the viewport.
  const ensureVisible = useCallback((smooth = true) => {
    const el = elRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let dx = 0;
    let dy = 0;
    if (r.width > vw - EDGE * 2) dx = 0;
    else if (r.left < EDGE) dx = EDGE - r.left;
    else if (r.right > vw - EDGE) dx = vw - EDGE - r.right;
    if (r.height > vh - EDGE * 2) dy = EDGE - r.top;
    else if (r.top < EDGE) dy = EDGE - r.top;
    else if (r.bottom > vh - EDGE) dy = vh - EDGE - r.bottom;
    if (!dx && !dy) return;
    const opts = { type: "spring", stiffness: 380, damping: 32 };
    if (smooth) {
      animate(x, x.get() + dx, opts);
      animate(y, y.get() + dy, opts);
    } else {
      x.set(x.get() + dx);
      y.set(y.get() + dy);
    }
  }, [x, y]);

  // Restore the saved position, then keep it valid when the screen size changes.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
        x.set(saved.x);
        y.set(saved.y);
      }
    } catch {}
    const raf = requestAnimationFrame(() => ensureVisible(false));
    const onResize = () => ensureVisible(false);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [storageKey, x, y, ensureVisible]);

  const startDrag = useCallback((e) => controls.start(e), [controls]);

  const guardClick = useCallback(
    (fn) => (e) => {
      if (draggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (fn) fn(e);
    },
    []
  );

  const dragProps = {
    drag: true,
    dragControls: controls,
    dragListener: false,
    dragMomentum: false,
    dragElastic: 0.06,
    dragConstraints: constraintsRef,
    onDragStart: () => {
      draggedRef.current = true;
    },
    onDragEnd: () => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ x: x.get(), y: y.get() }));
      } catch {}
      // Let the click that follows pointer-up see the flag, then clear it.
      setTimeout(() => {
        draggedRef.current = false;
      }, 120);
    },
  };

  return { x, y, elRef, constraintsRef, dragProps, startDrag, guardClick, ensureVisible };
}
